/**
 * Utilidades de calendario: .ics descargable y enlace a Google Calendar.
 *
 * Eventos y meetings guardan la fecha como Date y la hora aparte, en un string
 * "HH:MM". El día se lee en UTC (que es como se guardó la fecha de calendario)
 * y la hora se interpreta en la zona del navegador, que es la del club.
 */

const DEFAULT_DURATION = { event: 120, meeting: 60 };

const pad2 = (n) => String(n).padStart(2, "0");

/** "YYYY-MM-DD" en hora local, que es lo que espera <input type="date">. */
export const toDateInput = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** "HH:MM" en hora local, que es lo que espera <input type="time">. */
export const toTimeInput = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

/**
 * Valores iniciales de los formularios: hoy y la próxima media hora en punto.
 *
 * Se derivan de un mismo instante, así que si redondear cruza la medianoche
 * (por ejemplo a las 23:45) la fecha avanza con la hora y nunca queda un
 * momento en el pasado.
 */
export function defaultWhen(now = new Date()) {
  const d = new Date(now);
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() + (30 - (d.getMinutes() % 30)));
  return { date: toDateInput(d), time: toTimeInput(d) };
}

/** Combina la fecha (Date/ISO) con la hora "HH:MM". Sin hora → todo el día. */
export function buildRange(date, time, minutes = DEFAULT_DURATION.event) {
  if (!date) return null;

  const base = new Date(date);
  if (Number.isNaN(base.getTime())) return null;

  // El día se toma en UTC para no desplazarlo según la zona del visitante
  const y = base.getUTCFullYear();
  const m = base.getUTCMonth();
  const d = base.getUTCDate();

  const match = /^(\d{1,2}):(\d{2})/.exec((time || "").trim());
  if (!match) {
    const start = new Date(y, m, d);
    const end = new Date(y, m, d + 1);
    return { start, end, allDay: true };
  }

  const start = new Date(y, m, d, Number(match[1]), Number(match[2]));
  const end = new Date(start.getTime() + minutes * 60000);
  return { start, end, allDay: false };
}

const pad = (n) => String(n).padStart(2, "0");

/** YYYYMMDD en hora local (para eventos de todo el día). */
const asDate = (dt) => `${dt.getFullYear()}${pad(dt.getMonth() + 1)}${pad(dt.getDate())}`;

/** YYYYMMDDTHHMMSSZ en UTC (para eventos con hora). */
const asUTC = (dt) =>
  `${dt.getUTCFullYear()}${pad(dt.getUTCMonth() + 1)}${pad(dt.getUTCDate())}` +
  `T${pad(dt.getUTCHours())}${pad(dt.getUTCMinutes())}00Z`;

/**
 * Enlace que abre Google Calendar con el formulario ya relleno.
 *
 * `meet: true` añade vcon=meet, que crea la videollamada de Google Meet al
 * vuelo. Ojo con la hora: sin ella el rango sale en formato de día completo
 * (YYYYMMDD/YYYYMMDD) y Google marca "Todo el día", lo que deja los campos de
 * hora bloqueados. Quien quiera un evento con hora debe pasar `time`.
 */
export function googleCalendarUrl({ title, date, time, minutes, details, location, meet }) {
  const range = buildRange(date, time, minutes);
  if (!range) return null;

  const dates = range.allDay
    ? `${asDate(range.start)}/${asDate(range.end)}`
    : `${asUTC(range.start)}/${asUTC(range.end)}`;

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title || "",
    dates,
  });
  if (details) params.set("details", details);
  if (location) params.set("location", location);
  if (meet) params.set("vcon", "meet");

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** Escapa según RFC 5545: barra, coma, punto y coma y saltos de línea. */
const escapeText = (value = "") =>
  String(value)
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");

/** Octetos que ocupa un carácter en UTF-8. */
const charOctets = (char) => {
  const cp = char.codePointAt(0);
  if (cp < 0x80) return 1;
  if (cp < 0x800) return 2;
  if (cp < 0x10000) return 3;
  return 4;
};

const octets = (str) => {
  let total = 0;
  for (const char of str) total += charOctets(char);
  return total;
};

/**
 * Pliega las líneas a 75 octetos, como exige el RFC 5545.
 *
 * Se mide en octetos UTF-8, no en caracteres: una línea de tildes ocupa el
 * doble de lo que sugiere `length`. Se recorre por puntos de código (for…of)
 * para no partir por la mitad un par suplente, como los emoji.
 */
const fold = (line) => {
  if (octets(line) <= 75) return line;

  const chunks = [];
  let current = "";
  let size = 0;
  let limit = 75; // las líneas de continuación empiezan por un espacio

  for (const char of line) {
    const width = charOctets(char);
    if (size + width > limit) {
      chunks.push(current);
      current = char;
      size = width;
      limit = 74;
    } else {
      current += char;
      size += width;
    }
  }
  if (current) chunks.push(current);

  return chunks.map((c, i) => (i === 0 ? c : " " + c)).join("\r\n");
};

/** Genera el contenido de un .ics con un único evento. */
export function buildICS({ uid, title, date, time, minutes, details, location, url }) {
  const range = buildRange(date, time, minutes);
  if (!range) return null;

  const start = range.allDay
    ? `DTSTART;VALUE=DATE:${asDate(range.start)}`
    : `DTSTART:${asUTC(range.start)}`;
  const end = range.allDay
    ? `DTEND;VALUE=DATE:${asDate(range.end)}`
    : `DTEND:${asUTC(range.end)}`;

  const description = [details, url].filter(Boolean).join("\n\n");

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//DrivingYourDream Club//ES",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid || Date.now()}@drivingyourdream`,
    `DTSTAMP:${asUTC(new Date())}`,
    start,
    end,
    `SUMMARY:${escapeText(title)}`,
  ];

  if (description) lines.push(`DESCRIPTION:${escapeText(description)}`);
  if (location) lines.push(`LOCATION:${escapeText(location)}`);
  if (url) lines.push(`URL:${escapeText(url)}`);

  lines.push("END:VEVENT", "END:VCALENDAR");

  return lines.map(fold).join("\r\n");
}

/** Nombre de archivo legible a partir del título. */
const slugify = (value = "evento") =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase()
    .slice(0, 50) || "evento";

/** Descarga el .ics. Devuelve false si faltan datos de fecha. */
export function downloadICS(data) {
  const content = buildICS(data);
  if (!content) return false;

  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const href = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = href;
  link.download = `${slugify(data.title)}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Damos margen a Safari antes de liberar el objeto
  setTimeout(() => URL.revokeObjectURL(href), 1000);
  return true;
}

export { DEFAULT_DURATION };
